import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Mail, Loader2, LogOut, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { OtpInput } from '@/components/auth/OtpInput';

export const EmailVerificationGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, signOut } = useAuth();
  const { toast } = useToast();
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(600);

  useEffect(() => {
    if (!user) {
      setIsVerified(false);
      return;
    }

    if (user.email_confirmed_at) {
      setIsVerified(true);
      return;
    }

    let cancelled = false;
    const checkVerification = async () => {
      try {
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();
        if (cancelled) return;
        if (error || !currentUser) {
          // Fail closed: do not silently grant verification on a network or
          // auth-service failure — keep the user on the verification screen so
          // an outage cannot bypass the email-confirmation gate. The user can
          // retry by reloading or hitting Resend.
          setIsVerified(false);
          return;
        }
        setIsVerified(!!currentUser.email_confirmed_at);
      } catch {
        if (!cancelled) setIsVerified(false);
      }
    };

    checkVerification();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.email_confirmed_at) {
        setIsVerified(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (isVerified !== false || otpExpiry <= 0) return;
    const timer = setTimeout(() => setOtpExpiry(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [isVerified, otpExpiry]);

  const handleVerifyOtp = async () => {
    if (!user?.email || otpCode.length !== 6) return;
    setVerifying(true);
    try {
      // Use 'email' type — works for both signup-confirmation and
      // magiclink/recovery OTPs (the edge function uses generateLink({ type: 'magiclink' }),
      // which produces a recovery-type OTP for already-confirmed users).
      const { data, error } = await supabase.auth.verifyOtp({
        email: user.email,
        token: otpCode,
        type: 'email',
      });

      if (error) {
        toast({
          title: "Verification Failed",
          description: error.message === 'Token has expired or is invalid'
            ? "This code has expired. Click 'Resend code' to get a new one."
            : error.message,
          variant: "destructive",
        });
        return;
      }

      if (data.session) {
        setVerified(true);
        toast({
          title: "Email Verified!",
          description: "Your account is ready. Welcome to CreatorVault!",
        });
        setTimeout(() => setIsVerified(true), 1500);
      }
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleResendEmail = async () => {
    if (!user?.email) return;
    setIsResending(true);
    try {
      // 🛡️  Use Supabase's native signup-resend instead of the
      // send-signup-otp edge function. The edge function calls
      // admin.generateLink({ type: 'magiclink' }) which sets
      // `recovery_sent_at` and INVALIDATES the original signup token —
      // the root cause of the May 11 stranded-creators incident.
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) {
        toast({
          title: "Error resending code",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Verification code sent!",
          description: "Check your inbox for the 6-digit code",
        });
        setResendCooldown(60);
        setOtpExpiry(600);
        setOtpCode('');
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to resend verification code",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  if (loading || isVerified === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-brand/30 border-t-brand rounded-full animate-spin" />
          <div className="absolute inset-0 w-12 h-12 bg-brand/10 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isVerified && !verified) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="border-border/50 bg-gradient-card shadow-vault">
            <CardHeader className="text-center pb-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Shield className="w-8 h-8 text-primary" />
                <span className="text-2xl font-bold text-gradient-primary">
                  CreatorVault
                </span>
              </div>
              <CardTitle className="text-2xl">
                Enter Verification Code
              </CardTitle>
              <CardDescription>
                We sent a 6-digit code to <strong>{user.email}</strong>
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">
              <OtpInput value={otpCode} onChange={setOtpCode} onComplete={handleVerifyOtp} />

              {otpExpiry > 0 ? (
                <p className="text-center text-xs text-muted-foreground">
                  Code expires in {Math.floor(otpExpiry / 60)}:{(otpExpiry % 60).toString().padStart(2, '0')}
                </p>
              ) : (
                <p className="text-center text-xs text-destructive">
                  Code expired — request a new one below
                </p>
              )}

              <Button
                onClick={handleVerifyOtp}
                disabled={otpCode.length !== 6 || verifying || otpExpiry <= 0}
                variant="default"
                className="w-full"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>

              <Button
                onClick={handleResendEmail}
                disabled={isResending || resendCooldown > 0}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend code in ${resendCooldown}s`
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Resend Code
                  </>
                )}
              </Button>
              
              <Button
                onClick={signOut}
                variant="ghost"
                className="w-full"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Check your spam folder if you don't see the email
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4 animate-in zoom-in-50 duration-300" />
          <p className="text-lg font-semibold">Verified!</p>
          <p className="text-sm text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
